import { Link } from '@tanstack/react-router';
import { ArrowRight, type LucideIcon } from 'lucide-react';
import type { HTMLAttributes, ReactNode } from 'react';

import type { ObrasLink } from '../../features/obras/search.js';
import { cn } from '../../lib/cn.js';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Superfície interna: usada quando o card já está dentro de outro card. */
  readonly sunken?: boolean | undefined;
  readonly interactive?: boolean | undefined;
}

/**
 * Card não tem borda nem sombra: o que o separa do fundo é o tom (design.md §1).
 */
export function Card({ sunken, interactive, className, children, ...rest }: CardProps) {
  return (
    <div
      className={cn(
        'rounded-lg',
        sunken ? 'bg-muted' : 'bg-card',
        interactive && 'transition-colors duration-100 hover:bg-muted',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string | undefined;
  action?: ReactNode | undefined;
}) {
  return (
    <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-3">
      <div>
        <h2 className="text-[15px] font-semibold tracking-tight">{title}</h2>
        {description ? (
          <p className="mt-0.5 text-[13px] text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {action}
    </div>
  );
}

/**
 * Uma seção de formulário longo: o que se ajusta, com o que aquilo muda escrito
 * embaixo do título.
 *
 * O ícone não decora — é o que dá ao olho um ponto de entrada por seção quando a
 * página tem quatro delas e se procura uma. Um campo "fuso horário" sozinho não
 * diz a ninguém que é ele quem decide quando uma visita conta como vencida.
 */
export function FormSection({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <Card className="p-6">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-md bg-muted">
          <Icon size={16} strokeWidth={1.5} className="text-muted-foreground" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-[15px] font-semibold tracking-tight">{title}</h2>
          <p className="mt-1 text-[13px] text-muted-foreground">{description}</p>
          <div className="mt-5">{children}</div>
        </div>
      </div>
    </Card>
  );
}

/**
 * Indicador de painel. O destaque vem da densidade tipográfica do número,
 * não de cor de fundo — laranja aqui viraria ruído (design.md §3).
 */
export function Kpi({
  label,
  value,
  hint,
  tone = 'default',
  link,
}: {
  label: string;
  value: string;
  hint?: string | undefined;
  tone?: 'default' | 'success' | 'primary' | 'danger' | 'inverted' | undefined;
  /** Com link, o indicador inteiro leva à lista de obras que ele conta. */
  link?: ObrasLink | undefined;
}) {
  const inverted = tone === 'inverted';

  const valueTone = {
    default: 'text-foreground',
    success: 'text-success',
    primary: 'text-primary-strong',
    danger: 'text-danger',
    inverted: 'text-white',
  }[tone];

  const className = cn(
    'block rounded-lg p-5',
    inverted ? 'bg-foreground' : 'bg-card',
    link && 'transition-colors duration-100 hover:bg-accent',
  );

  const content = (
    <>
      <p
        className={cn(
          'flex items-center justify-between gap-2 text-[11px] font-medium tracking-[0.06em] uppercase',
          inverted ? 'text-white/55' : 'text-muted-foreground',
        )}
      >
        {label}
        {link ? <ArrowRight size={14} strokeWidth={1.5} aria-hidden /> : null}
      </p>
      <p className={cn('numeral mt-2.5 text-[28px] leading-none', valueTone)}>{value}</p>
      {hint ? (
        <p className={cn('mt-2 text-[13px]', inverted ? 'text-white/55' : 'text-muted-foreground')}>
          {hint}
        </p>
      ) : null}
    </>
  );

  if (link) {
    return (
      <Link to={link.to} search={link.search ?? {}} className={className}>
        {content}
      </Link>
    );
  }

  return <div className={className}>{content}</div>;
}
